import { useState, useEffect, useContext, createContext } from 'react';
import dockerHubService from '../services/dockerHubService';

const DockerHubContext = createContext();

export const useDockerHub = () => {
  const context = useContext(DockerHubContext);
  if (!context) {
    throw new Error('useDockerHub must be used within a DockerHubProvider');
  }
  return context;
};

export const DockerHubProvider = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [username, setUsername] = useState(null);
  const [loading, setLoading] = useState(false);

  // Function to check and update auth state
  const checkAuthState = () => {
    const authenticated = dockerHubService.isAuthenticated();
    const currentUsername = dockerHubService.getUsername();
    setIsAuthenticated(authenticated);
    setUsername(currentUsername);
  };

  useEffect(() => {
    checkAuthState();

    // Listen for storage changes (e.g., from other tabs or Settings page)
    const handleStorageChange = (e) => {
      if (e.key === 'dockerHubToken' || e.key === 'dockerHubUsername') {
        checkAuthState();
      }
    };

    window.addEventListener('storage', handleStorageChange);

    // Also check periodically in case of same-tab changes
    const interval = setInterval(checkAuthState, 1000);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
      clearInterval(interval);
    };
  }, []);

  const login = async (username, password) => {
    setLoading(true);
    try {
      const result = await dockerHubService.login(username, password);
      if (result.success) {
        checkAuthState(); // Immediately update state
      }
      return result;
    } catch (error) {
      console.error('Docker Hub login error:', error);
      return { success: false, error: 'Login failed' };
    } finally {
      setLoading(false);
    }
  };

  const logout = async () => {
    setLoading(true);
    try {
      await dockerHubService.logout();
      checkAuthState(); // Immediately update state
      return { success: true };
    } catch (error) {
      console.error('Docker Hub logout error:', error);
      return { success: false, error: 'Logout failed' };
    } finally {
      setLoading(false);
    }
  };

  const getRepositories = async (namespace = null) => {
    if (!isAuthenticated) {
      return { success: false, error: 'Not authenticated' };
    }
    return await dockerHubService.getRepositories(namespace);
  };

  const searchRepositories = async (query, limit = 25) => {
    return await dockerHubService.searchRepositories(query, limit);
  };

  const getRepositoryDetails = async (namespace, repository) => {
    return await dockerHubService.getRepositoryDetails(namespace, repository);
  };

  const getRepositoryTags = async (namespace, repository) => {
    return await dockerHubService.getRepositoryTags(namespace, repository);
  };

  const getUserProfile = async () => {
    if (!isAuthenticated) {
      return { success: false, error: 'Not authenticated' };
    }
    return await dockerHubService.getUserProfile();
  };

  const value = {
    isAuthenticated,
    username,
    loading,
    login,
    logout,
    getRepositories,
    searchRepositories,
    getRepositoryDetails,
    getRepositoryTags,
    getUserProfile,
    checkAuthState
  };

  return (
    <DockerHubContext.Provider value={value}>
      {children}
    </DockerHubContext.Provider>
  );
};